import { assign, Machine, MachineConfig, MachineOptions } from 'xstate';

type Context = {
  searchKeyword: string;
  locationKeyword: string;
};

const initialContext: Context = {
  searchKeyword: '',
  locationKeyword: '',
};

interface KeywordSearchSchema {
  states: {
    idle: Record<string, unknown>;
    searchReady: Record<string, unknown>;
  };
}

type KeywordTransitions =
  | { type: 'UPDATE_SEARCH_KEYWORD' }
  | { type: 'UPDATE_LOCATION_KEYWORD' }
  | { type: 'ON_ENTER' }
  | { type: 'ON_SEARCH_BUTTON_CLICK' }
  | { type: 'RESET_TO_IDLE' };

const keywordMachineConfig: MachineConfig<
  Context,
  KeywordSearchSchema,
  KeywordTransitions
> = {
  id: 'keywordSearchMachine',
  initial: 'idle',
  context: initialContext,
  states: {
    idle: {
      on: {
        UPDATE_SEARCH_KEYWORD: {
          actions: ['updateSearchKeyword'],
        },
        UPDATE_LOCATION_KEYWORD: {
          actions: ['updateLocationKeyword'],
        },
        ON_ENTER: {
          target: 'searchReady',
          cond: 'locationValid',
          actions: ['updateKeywords'],
        },
        ON_SEARCH_BUTTON_CLICK: {
          target: 'searchReady',
          cond: 'locationValid',
          actions: ['updateKeywords'],
        },
      },
    },
    searchReady: {
      on: {
        RESET_TO_IDLE: {
          target: 'idle',
          actions: ['resetContext'],
        },
      },
    },
  },
};

const keywordMachineOptions: Partial<MachineOptions<Context, any>> = {
  guards: {
    locationValid: (_context, event) => {
      return event.locationValid;
    },
  },
  actions: {
    updateSearchKeyword: assign((context, event) => {
      const { searchKeyword } = event;

      return { ...context, searchKeyword };
    }),
    updateLocationKeyword: assign((context, event) => {
      const { locationKeyword } = event;

      return { ...context, locationKeyword };
    }),
    updateKeywords: assign((context, event) => {
      const { suggestion } = event;

      if (suggestion) {
        return { ...context, searchKeyword: suggestion };
      }
      return { ...context };
    }),
    resetContext: assign((context) => {
      return {
        ...context,
        initialContext,
      };
    }),
  },
};

const keywordSearchMachine = Machine<
  Context,
  KeywordSearchSchema,
  KeywordTransitions
>(keywordMachineConfig, keywordMachineOptions);

export default keywordSearchMachine;
